// src/components/agency/AgencyDashboard.tsx
import React, { useState } from 'react';
import { Users, Shield, Activity } from 'lucide-react';
import { AgencyList } from './AgencyList';
import { AgencyDetail } from './AgencyDetail';
import { SystemStats } from './SystemStats';

type TabType = 'list' | 'system';

interface AgencyDashboardProps {
  onClose?: () => void;
}

export const AgencyDashboard: React.FC<AgencyDashboardProps> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType | 'detail'>('list');
  const [selectedAgencyId, setSelectedAgencyId] = useState<string | null>(null);

  const tabs = [
    { id: 'list' as TabType, label: '代理店一覧', icon: Users, description: '登録されている代理店の管理' },
    { id: 'system' as TabType, label: 'システム', icon: Activity, description: 'システム全体の監視' },
  ];

  const handleAgencySelect = (agencyId: string) => {
    setSelectedAgencyId(agencyId);
    setActiveTab('detail');
  };

  const handleBackToList = () => {
    setSelectedAgencyId(null);
    setActiveTab('list');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-blue-600" />
              <h1 className="text-xl font-semibold text-gray-900">いきいきスマイル共済 管理システム</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">管理者モード</div>
              {onClose && (
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* タブナビゲーション */}
        {activeTab !== 'detail' && (
          <div className="bg-white rounded-lg shadow-sm mb-6 overflow-hidden">
            <nav className="flex">
              {tabs.map((tab, index) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex-1 flex items-center justify-center space-x-2 px-4 py-4 font-medium text-sm
                      transition-all duration-200 border-b-2
                      ${
                        isActive
                          ? 'bg-blue-50 text-blue-600 border-blue-500'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent'
                      }
                      ${index > 0 ? 'border-l border-gray-200' : ''}
                    `}
                    title={tab.description}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        {/* タブコンテンツ */}
        <div className="bg-white rounded-lg shadow-sm">
          {activeTab === 'list' && <AgencyList onAgencySelect={handleAgencySelect} />}

          {activeTab === 'detail' && selectedAgencyId && (
            <AgencyDetail agencyId={selectedAgencyId} onBack={handleBackToList} />
          )}

          {activeTab === 'system' && <SystemStats />}
        </div>

        {/* フッター */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Firebase認証システム v2.0 | 管理画面</p>
          <p className="mt-1">システムに関するお問い合わせは開発チームまでご連絡ください</p>
        </div>
      </div>
    </div>
  );
};
